'use client';

/** @jsxImportSource react */

import { createContext, type ReactNode, useContext } from 'react';

import {
  fallbackFeatureFlagSnapshot,
  type FeatureFlagKey,
  type FeatureFlagSnapshot,
} from '@onark/observability';

/**
 * The client half of the flag seam. There is no second implementation here:
 * the decision was already made on the server by `server/featureFlags.ts`, and
 * this only carries it down the tree.
 *
 * That direction is deliberate, and it is what keeps requirement three honest:
 *
 *  - **A client component can never hang on PostHog,** because it never asks
 *    PostHog. No `/flags` round trip on mount, no loading state, no layout
 *    shift from a banner that appears a second late.
 *  - **Client and server always agree,** so a flagged surface cannot render on
 *    the server and vanish on hydration.
 *  - **Preview and local dev behave,** where posthog-js is deliberately not
 *    even initialized (`isNonProductionAnalyticsHost`) and a browser-side flag
 *    lookup would be dead on arrival.
 *
 * Without a provider above it, `useFeatureFlag` answers with the flag's
 * declared fallback. That is the safe default rather than a crash, but it means
 * a surface flagged inside a tree whose layout never resolved the snapshot will
 * silently sit at its fallback forever, so wire the provider in the layout
 * (see app/admin/layout.tsx) rather than around the component.
 */
const FeatureFlagContext = createContext<FeatureFlagSnapshot>(
  fallbackFeatureFlagSnapshot()
);

export function FeatureFlagProvider({
  children,
  flags,
}: {
  children: ReactNode;
  flags: FeatureFlagSnapshot;
}) {
  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/** Is this flag on for the church whose admin session rendered this tree? */
export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  return useContext(FeatureFlagContext)[flag];
}
